import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { useModals } from "./ModalProvider";
import { INDUSTRY_DETAILS } from "../../data/content";
import {
  ShieldCheck, Database, BadgeCheck, Lock, AlertTriangle, ArrowRight, FileText, Layers,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { motion } from "framer-motion";

export default function IndustryDetailModal({ industry, onOpenChange }) {
  const { openBookConsultation } = useModals();
  const open = !!industry;
  const detail = industry ? INDUSTRY_DETAILS[industry.id] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="industry-detail-modal"
      >
        {industry && detail && (
          <>
            {/* Header with image */}
            <div className="relative h-40 sm:h-52 overflow-hidden shrink-0">
              <img
                src={industry.image}
                alt={industry.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, ${industry.accent}20 0%, ${industry.accent}D9 60%, ${industry.accent} 100%)`,
                }}
              />
              <div className="absolute inset-0 grid-pattern opacity-20" />
              <div className="relative h-full flex items-end p-5 sm:p-7 text-white">
                <div>
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
                    <Layers className="h-3 w-3" /> Industry Playbook
                  </div>
                  <DialogHeader className="mt-2 text-left">
                    <DialogTitle className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight text-white drop-shadow">
                      {industry.title}
                    </DialogTitle>
                  </DialogHeader>
                </div>
              </div>
            </div>

            <div className="p-5 sm:p-7 overflow-y-auto flex-1">
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {detail.overview}
              </p>

              <Tabs defaultValue="compliance" className="mt-6">
                <TabsList className="w-full grid grid-cols-4 h-auto p-1 rounded-full bg-muted">
                  <TabsTrigger value="compliance" data-testid="industry-tab-compliance" className="rounded-full text-xs sm:text-sm font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <BadgeCheck className="h-3.5 w-3.5 mr-1.5" /> Compliance
                  </TabsTrigger>
                  <TabsTrigger value="data" data-testid="industry-tab-data" className="rounded-full text-xs sm:text-sm font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <Database className="h-3.5 w-3.5 mr-1.5" /> Data
                  </TabsTrigger>
                  <TabsTrigger value="controls" data-testid="industry-tab-controls" className="rounded-full text-xs sm:text-sm font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Controls
                  </TabsTrigger>
                  <TabsTrigger value="threats" data-testid="industry-tab-threats" className="rounded-full text-xs sm:text-sm font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                    <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Threats
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="compliance" className="mt-5" data-testid="industry-tab-content-compliance">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <ComplianceCol title="IT Compliance" items={detail.itCompliance} icon={FileText} tone="primary" />
                    <ComplianceCol title="Data Privacy" items={detail.dataCompliance} icon={Lock} tone="accent" />
                    <ComplianceCol title="Security Frameworks" items={detail.securityCompliance} icon={ShieldCheck} tone="secondary" />
                  </div>
                </TabsContent>

                <TabsContent value="data" className="mt-5" data-testid="industry-tab-content-data">
                  <div className="rounded-2xl border border-border bg-card p-5">
                    <div className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-1.5">
                      <Database className="h-3.5 w-3.5" /> Critical data assets we help you protect
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      {detail.dataAssets.map((d, i) => (
                        <motion.div
                          key={d}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: i * 0.05 }}
                          className="flex items-center gap-3 rounded-xl border border-border bg-background p-3"
                        >
                          <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0" style={{ background: `${industry.accent}18`, color: industry.accent }}>
                            <Database className="h-4 w-4" />
                          </div>
                          <span className="text-sm font-medium">{d}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="controls" className="mt-5" data-testid="industry-tab-content-controls">
                  <div className="grid sm:grid-cols-2 gap-3">
                    {detail.controls.map((c, i) => (
                      <motion.div
                        key={c}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: i * 0.04 }}
                        className="flex items-start gap-3 rounded-xl border border-border bg-card p-3.5"
                      >
                        <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                          <ShieldCheck className="h-4 w-4" />
                        </div>
                        <span className="text-sm leading-relaxed text-foreground/90">{c}</span>
                      </motion.div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="threats" className="mt-5" data-testid="industry-tab-content-threats">
                  <div className="grid gap-3">
                    {detail.threats.map((t, i) => (
                      <motion.div
                        key={t}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.05 }}
                        className="flex items-center gap-3 rounded-xl border border-secondary/25 bg-secondary/5 p-3.5"
                      >
                        <div className="h-9 w-9 rounded-lg bg-secondary/15 text-secondary grid place-items-center shrink-0">
                          <AlertTriangle className="h-4 w-4" />
                        </div>
                        <span className="text-sm font-medium">{t}</span>
                      </motion.div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className="p-5 sm:p-6 border-t border-border bg-muted/40 dark:bg-muted/20 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
              <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
                Get a <span className="font-semibold text-foreground">{industry.title}-specific</span> readiness assessment prepared by Techmate.
              </div>
              <Button
                onClick={() => { onOpenChange(false); openBookConsultation(); }}
                data-testid="industry-detail-consult-btn"
                className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Talk to a {industry.title} specialist <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const toneMap = {
  primary: "bg-primary/10 text-primary border-primary/25",
  accent: "bg-accent/15 text-primary border-accent/30",
  secondary: "bg-secondary/10 text-secondary border-secondary/25",
};

function ComplianceCol({ title, items, icon: Icon, tone }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${toneMap[tone]}`}>
        <Icon className="h-3 w-3" /> {title}
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-foreground/90">
            <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
